import {
  createClient,
  AnamEvent,
  type AnamClient,
  type EventCallbacks,
  type MessageStreamEvent,
} from "@anam-ai/js-sdk";
import { api } from "./api";
import type { AvatarSpeechType } from "./exchangeTiming";

interface SpeechContext {
  exchangeId?: string;
  speechType: AvatarSpeechType;
}

export interface AnamSessionConfig {
  sessionToken: string;
}
interface AvatarCallbacks {
  onStreamReady?: () => void;
  onStartTalking?: (context?: SpeechContext) => void;
  onStopTalking?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
  onPlaybackBlocked?: (blocked: boolean) => void;
}
const VIDEO_ELEMENT_ID = "patient-avatar-video";
const STREAM_READY_TIMEOUT_MS = 45_000;

export class AnamService {
  private client: AnamClient | null = null;
  private sessionId: string | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private videoStream: MediaStream | null = null;
  private audioStream: MediaStream | null = null;
  private callbacks: AvatarCallbacks = {};
  private removers: Array<() => void> = [];
  private streamReadyTimer: number | null = null;
  private resolveReady: ((ready: boolean) => void) | null = null;
  private connectionEstablished = false;
  private sessionReady = false;
  private dataChannelReady = false;
  private playbackReady = false;
  private playbackBlocked = false;
  private playbackAttempt: {
    element: HTMLVideoElement;
    stream: MediaStream;
    promise: Promise<boolean>;
  } | null = null;
  private streamReady = false;
  private isAvatarSpeaking = false;
  private activeSpeech: {
    id: string;
    context?: SpeechContext;
    stream: ReturnType<AnamClient["createTalkMessageStream"]>;
  } | null = null;
  private disconnectPromise: Promise<void> | null = null;
  private lifecycleGeneration = 0;

  selectAvatarForCase(): { avatarId: string } {
    return { avatarId: "anam-patient-avatar" };
  }

  async initializeAvatar(
    config: AnamSessionConfig,
    _avatarId: string,
    callbacks: AvatarCallbacks,
  ): Promise<boolean> {
    if (!config.sessionToken) {
      callbacks.onError?.(new Error("Anam session token is missing"));
      return false;
    }
    const element = this.videoElement;
    const cleanup = this.disconnect();
    const generation = this.lifecycleGeneration;
    await cleanup;
    if (generation !== this.lifecycleGeneration) return false;
    this.videoElement =
      (document.getElementById(VIDEO_ELEMENT_ID) as HTMLVideoElement | null) ??
      element;
    if (!this.videoElement) {
      callbacks.onError?.(new Error("Avatar video element is unavailable"));
      return false;
    }
    this.videoElement.id ||= VIDEO_ELEMENT_ID;
    this.callbacks = callbacks;
    let client: AnamClient;
    try {
      client = createClient(config.sessionToken, {
        disableInputAudio: true,
        metrics: { disableClientMetrics: true },
      });
    } catch {
      this.fail(
        new Error("Could not initialize Anam. Please try reconnecting"),
      );
      return false;
    }
    this.client = client;
    const ready = new Promise<boolean>((resolve) => {
      this.resolveReady = resolve;
    });
    const listen = <K extends AnamEvent>(
      event: K,
      callback: EventCallbacks[K],
    ) => {
      client.addListener(event, callback);
      this.removers.push(() => client.removeListener(event, callback));
    };
    // Register every listener before starting the SDK; startup events can fire immediately.
    listen(AnamEvent.CONNECTION_ESTABLISHED, () => {
      if (this.client !== client) return;
      this.connectionEstablished = true;
      this.checkReady();
    });
    listen(AnamEvent.DATA_CHANNEL_OPEN, () => {
      if (this.client !== client) return;
      this.dataChannelReady = true;
      this.checkReady();
    });
    listen(AnamEvent.SESSION_READY, (sessionId) => {
      if (this.client !== client) return;
      this.sessionId = sessionId;
      void api
        .avatarSessionReady(sessionId)
        .then(() => {
          if (this.client !== client) return;
          this.sessionReady = true;
          this.checkReady();
        })
        .catch(() => {
          if (this.client === client)
            this.fail(
              new Error(
                "Could not register avatar session. Please try reconnecting",
              ),
            );
        });
    });
    listen(AnamEvent.VIDEO_STREAM_STARTED, (stream) => {
      if (this.client !== client) return;
      this.videoStream = stream;
      // The SDK sets srcObject after emitting this event. Attach once that finishes.
      queueMicrotask(() => {
        if (this.client === client) this.attachMedia();
      });
    });
    listen(AnamEvent.AUDIO_STREAM_STARTED, (stream) => {
      if (this.client !== client) return;
      this.audioStream = stream;
      queueMicrotask(() => {
        if (this.client === client) this.attachMedia();
      });
    });
    listen(AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED, (event) => {
      if (this.client === client) this.handlePersonaMessage(event);
    });
    listen(AnamEvent.TALK_STREAM_INTERRUPTED, (id) => {
      if (this.client === client && this.activeSpeech?.id === id)
        this.finishSpeech();
    });
    listen(AnamEvent.CONNECTION_CLOSED, () => {
      if (this.client !== client) return;
      const onDisconnected = this.callbacks.onDisconnected;
      void this.disconnect();
      onDisconnected?.();
    });
    this.streamReadyTimer = window.setTimeout(() => {
      if (this.client === client && !this.streamReady)
        this.fail(
          new Error(
            "Timed out waiting for Anam avatar stream. Please try reconnecting",
          ),
        );
    }, STREAM_READY_TIMEOUT_MS);
    try {
      await client.streamToVideoElement(this.videoElement.id);
      if (generation !== this.lifecycleGeneration) {
        await this.cleanupClient(client);
        return false;
      }
      return await ready;
    } catch {
      // SDK errors may contain credentials or provider payloads; expose a fixed message.
      if (generation !== this.lifecycleGeneration) {
        await this.cleanupClient(client);
        return false;
      }
      this.sessionId ||= client.getActiveSessionId();
      this.fail(
        new Error("Could not connect to Anam. Please try reconnecting"),
      );
      await this.disconnectPromise;
      return false;
    }
  }

  attachToElement(element: HTMLVideoElement): void {
    this.videoElement = element;
    element.id ||= VIDEO_ELEMENT_ID;
    element.autoplay = true;
    element.playsInline = true;
    if (this.videoStream) this.attachMedia();
  }

  detachFromElement(element: HTMLVideoElement): void {
    element.srcObject = null;
    if (this.videoElement === element) {
      this.videoElement = null;
      this.playbackAttempt = null;
      this.playbackReady = false;
    }
  }

  private attachMedia(): void {
    if (!this.videoElement || !this.videoStream) return;
    // Keep the SDK's video stream target while ensuring separate audio tracks play too.
    for (const track of this.audioStream?.getAudioTracks() ?? []) {
      if (!this.videoStream.getTracks().includes(track))
        this.videoStream.addTrack(track);
    }
    const element = this.videoElement;
    // Assigning srcObject again can abort an in-flight play(), even for the same stream.
    if (element.srcObject !== this.videoStream)
      element.srcObject = this.videoStream;
    if (!this.playbackBlocked) void this.startPlayback(element);
  }

  private startPlayback(
    element: HTMLVideoElement,
    retryAbort = true,
  ): Promise<boolean> {
    const client = this.client;
    const stream = this.videoStream;
    if (!client || !stream || element.srcObject !== stream)
      return Promise.resolve(false);
    if (
      this.playbackAttempt?.element === element &&
      this.playbackAttempt.stream === stream
    ) {
      return this.playbackAttempt.promise;
    }
    const attempt = { element, stream, promise: Promise.resolve(false) };
    this.playbackAttempt = attempt;
    const isCurrent = () =>
      this.client === client &&
      this.videoElement === element &&
      element.srcObject === stream &&
      this.playbackAttempt === attempt;
    let playing: Promise<void>;
    try {
      // Call synchronously so resumePlayback retains the button's user activation.
      playing = element.play();
    } catch (error) {
      playing = Promise.reject(error);
    }
    attempt.promise = playing
      .then(() => {
        if (!isCurrent()) return false;
        this.playbackReady = true;
        this.setPlaybackBlocked(false);
        this.checkReady();
        return true;
      })
      .catch((error: unknown) => {
        if (!isCurrent()) return false;
        const name =
          error instanceof Error ||
          (typeof error === "object" && error !== null)
            ? (error as { name?: string }).name
            : undefined;
        if (name === "AbortError") {
          // A stream update can interrupt playback without a permissions problem.
          // Retry once; later track events can retry again, and the stream timeout
          // still bounds startup if the browser keeps aborting.
          this.playbackAttempt = null;
          return retryAbort ? this.startPlayback(element, false) : false;
        }
        if (name === "NotAllowedError") {
          this.playbackReady = false;
          this.setPlaybackBlocked(true);
          this.checkReady();
          return false;
        }
        this.fail(
          new Error("Could not play avatar media. Please try reconnecting"),
        );
        return false;
      })
      .finally(() => {
        if (this.playbackAttempt === attempt) this.playbackAttempt = null;
      });
    return attempt.promise;
  }

  private setPlaybackBlocked(blocked: boolean): void {
    if (this.playbackBlocked === blocked) return;
    this.playbackBlocked = blocked;
    this.callbacks.onPlaybackBlocked?.(blocked);
  }

  isPlaybackBlocked(): boolean {
    return this.playbackBlocked;
  }

  resumePlayback(): Promise<boolean> {
    if (!this.videoElement) return Promise.resolve(false);
    return this.startPlayback(this.videoElement);
  }

  private checkReady(): void {
    if (
      this.streamReady ||
      !this.connectionEstablished ||
      !this.sessionReady ||
      !this.dataChannelReady ||
      !this.videoStream ||
      !this.audioStream
    )
      return;
    // All transport resources arrived; an explicit play click can finish startup.
    // Keep the remote connection alive instead of issuing another billable session.
    if (this.playbackBlocked) {
      this.clearTimeout();
      return;
    }
    if (!this.playbackReady) return;
    this.streamReady = true;
    this.clearTimeout();
    this.resolveReady?.(true);
    this.resolveReady = null;
    this.callbacks.onStreamReady?.();
  }

  async speak(text: string, context?: SpeechContext): Promise<void> {
    if (!text.trim()) return;
    if (!this.client || !this.streamReady)
      throw new Error("Avatar is not connected");
    // Replace any pending/playing response; never let overlapping responses survive.
    if (this.activeSpeech) this.interruptActiveSpeech();
    if (!this.client || !this.streamReady) return;
    const client = this.client;
    const id = crypto.randomUUID();
    const stream = client.createTalkMessageStream(id);
    this.activeSpeech = { id, stream, context };
    try {
      // A complete application response is one turn, unchanged. Cara 4 echoes the utterance ID.
      await stream.streamMessageChunk(text, true, id);
    } catch {
      if (this.client === client && this.activeSpeech?.id === id)
        this.finishSpeech();
      throw new Error("Could not send avatar speech");
    }
  }

  private handlePersonaMessage(event: MessageStreamEvent): void {
    const speech = this.activeSpeech;
    if (event.role !== "persona" || !speech) return;
    if (event.correlationId !== speech.id && event.utteranceId !== speech.id) return;
    // A short utterance may arrive with content and endOfSpeech in the same event.
    if (!event.interrupted && !this.isAvatarSpeaking && event.content) {
      this.isAvatarSpeaking = true;
      this.callbacks.onStartTalking?.(speech.context);
    }
    if (event.interrupted || event.endOfSpeech) {
      this.finishSpeech();
    }
  }

  async interrupt(): Promise<void> {
    // Also interrupt a submitted turn that has not started playing yet.
    this.interruptActiveSpeech();
  }
  private interruptActiveSpeech(): void {
    if (!this.client || !this.activeSpeech) return;
    try {
      this.client.interruptPersona();
    } catch {
      throw new Error("Could not interrupt avatar speech");
    } finally {
      this.finishSpeech();
    }
  }
  private finishSpeech(): void {
    this.activeSpeech = null;
    this.isAvatarSpeaking = false;
    this.callbacks.onStopTalking?.();
  }

  disconnect(): Promise<void> {
    ++this.lifecycleGeneration;
    if (this.disconnectPromise) return this.disconnectPromise;
    const client = this.client;
    const sessionId = this.sessionId ?? client?.getActiveSessionId();
    // Detach listeners/state before stopStreaming can emit synchronous close events.
    for (const remove of this.removers) remove();
    this.removers = [];
    this.clearTimeout();
    this.resolveReady?.(false);
    this.resolveReady = null;
    this.callbacks = {};
    this.playbackAttempt = null;
    this.playbackBlocked = false;
    if (this.videoElement) this.videoElement.srcObject = null;
    const tracks = new Set([
      ...(this.videoStream?.getTracks() ?? []),
      ...(this.audioStream?.getTracks() ?? []),
    ]);
    for (const track of tracks) track.stop();
    this.videoElement = null;
    this.videoStream = null;
    this.audioStream = null;
    this.client = null;
    this.sessionId = null;
    this.activeSpeech = null;
    this.isAvatarSpeaking = false;
    this.streamReady =
      this.sessionReady =
      this.connectionEstablished =
      this.dataChannelReady =
      this.playbackReady =
        false;
    // Start remote keepalive immediately, before pagehide returns.
    const remoteEnd = sessionId
      ? api.endAvatarSession(sessionId)
      : Promise.resolve();
    const localEnd = async () => {
      if (!client) return;
      try {
        client.interruptPersona();
      } catch {
        /* May not have connected yet. */
      }
      await client.stopStreaming();
    };
    this.disconnectPromise = Promise.allSettled([localEnd(), remoteEnd])
      .then((results) => {
        if (results.some((result) => result.status === "rejected"))
          console.warn("Avatar cleanup incomplete; server cleanup will retry");
      })
      .finally(() => {
        this.disconnectPromise = null;
      });
    return this.disconnectPromise;
  }

  private async cleanupClient(client: AnamClient): Promise<void> {
    const id = client.getActiveSessionId();
    await Promise.allSettled([
      client.stopStreaming(),
      id ? api.endAvatarSession(id) : Promise.resolve(),
    ]);
  }
  private fail(error: Error): void {
    const onError = this.callbacks.onError;
    void this.disconnect();
    onError?.(error);
  }
  private clearTimeout(): void {
    if (this.streamReadyTimer !== null)
      window.clearTimeout(this.streamReadyTimer);
    this.streamReadyTimer = null;
  }
  isConnected(): boolean {
    return Boolean(this.client && this.streamReady);
  }
  isSpeaking(): boolean {
    return this.isAvatarSpeaking;
  }
  canInterrupt(): boolean {
    return this.isConnected() && Boolean(this.activeSpeech);
  }
  getSpeakingState() {
    return {
      isSpeaking: this.isSpeaking(),
      canInterrupt: this.canInterrupt(),
      isConnected: this.isConnected(),
    };
  }
}
export const anamService = new AnamService();
